'use client'
import React, { useEffect, useState } from "react"
// styles:
import styles from "@/app/styles/hint.module.css"
import animation from "@/app/styles/animation.module.css"

export type HintProps = {
    position?: {x: number, y: number},
    element?: Element,
    elementOffset?: {x: number, y: number},
    text: string,
    links?: HintLink[],
    greyOverlay?: boolean
}

export type HintLink = {
    text: string,
    callback?: () => void
}

export default function Hint({ position, element, elementOffset, text, links, greyOverlay} : HintProps) {

    if (!position && !element)
        return null;

    const [renderPosition, setRenderPosition] = useState<{x: number, y: number} | null>(null)

    // The effect sets up a listener to move the position as the window resizes:
    useEffect(() => {
        function updateRenderPosition() {
            // privilege position
            if (position) {
                setRenderPosition(position);
            }
            else {
                const rect = element?.getBoundingClientRect();
                if (!rect) {
                    return null;
                }
                setRenderPosition({
                    x: rect.left + window.scrollX + (elementOffset?.x ?? 0),
                    y: rect.top + window.scrollY + (elementOffset?.y ?? 0)
                })
            }
        }

        updateRenderPosition();
        window.addEventListener('resize', updateRenderPosition);

        return () => window.removeEventListener("resize", updateRenderPosition);
    }, [])

    // Don't render until position is calculated
    if (!renderPosition) {
        return null;
    }

    const style: React.CSSProperties = {
        position: "absolute",
        top: renderPosition.y,
        left: renderPosition.x,
        // this needs to be matched in the animation:
        transform: 'translate(-20%, -100%)',
    }

    return (
        <>
        {greyOverlay && <div className={styles.greyOverlay} />}
        <div 
            className={`
                ${styles.hint}
                ${animation.hintExpand}
            `}
            style={style}
            key={text}
        >
            {text}
            <div>
                {links?.map(((link, i) => 
                <span key={i}>
                    <span 
                        className={styles.hintLink}
                        onClick={link.callback}
                    >
                        {link.text}
                    </span>
                    {(i < links.length) && 
                        <span>
                            &nbsp;
                        </span>
                    }
                </span>
                ))}
            </div>
        </div>
        </>
    )
}